import { LitElement, html, css, ifDefined, classMap } from '@hh/global.js'

class HHCustomSelect extends LitElement {
  static properties = {
    options: {
      type: Object,
    },
    name: {
      type: String,
    },
    active: {
      type: String,
    },
    lang: {
      type: Boolean,
    },
    position: {
      type: String,
    },
    _selected: {
      state: true,
    },
    _active: {
      state: true,
    },
    disabled: {
      type: Boolean,
      reflect: true,
    },
    defaultUnselected: {
      type: Boolean,
      reflect: true,
      attribute: 'default-unselected',
    },
    ctx: {
      type: String,
    },
    label: {
      type: String,
    }
  }

  constructor() {
    super()
    this.handleBodyClick = this.handleBodyClick.bind(this)
    this.uuid = Math.random() * (1000000 - 1) + 1
    this.position = 'bottom'
    this.ctx = ''
    this.mql = window.matchMedia('(min-width: 750px)')
  }

  connectedCallback() {
    super.connectedCallback()
    this._selected = this.active
    document.body.addEventListener('click', this.handleBodyClick)
    document.addEventListener('focusin', this.handleFocusIn.bind(this))

    if (this.ctx === 'variant') {
      if (this.mql.matches) {
        this.position = 'top'
      } else {
        this.position = 'bottom'
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.body.addEventListener('click', this.handleBodyClick)
    document.removeEventListener('focusin', this.handleFocusIn)
  }

  handleBodyClick(e) {
    const path = e.composedPath()
    if (
      path.some((node) => {
        return node === this
      })
    ) {
      return true
    }

    this._active && this.toggle()
  }

  toggle(e = false) {
    if (this.disabled) return
    if (e && e.type === 'keyup' && !~['ESCAPE'].indexOf(e.code.toUpperCase())) {
      return true
    }

    this._active = !this._active
  }

  handleFocusIn(e) {
    const path = e.composedPath()
    if (path.includes(this)) return
    this._active = false
  }

  handleKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      this._active = false
    }

    if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
      e.preventDefault()
      if (!this._active) {
        this.toggle(e)
      }
    }
  }

  handleKeyup(e) {
    if (e.key === 'Enter') {
      this.button.focus({ preventScroll: true })
    }
  }

  selectValue(e, value) {
    e.preventDefault()
    this._selected = value
    ;(this.querySelector('[slot="input"]') || {}).value = value
    this.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        composed: true,
        detail: {
          value,
        },
      })
    )
    this._active = false
  }

  get selectedLabel() {
    const option = this.options.find(
      (option) => option.value === this._selected
    )

    if (!option && this.defaultUnselected) return ''

    if (!option) return 'Invalid selected option'
    return option.label
  }

  sanitize(value) {
    return value.replace('&amp;', '&')
  }

  get button() {
    return this.renderRoot?.querySelector('button') ?? null
  }

  render() {
    return html`
      <div
        class="container"
        @keydown=${this.handleKeydown}
        @keyup=${this.handleKeyup}
      >
        <div id="${this.uuid}-label">
          <slot name="title"></slot>
        </div>
        <button
          @click=${this.toggle}
          type="button"
          class="button"
          aria-expanded="${this._active}"
          aria-controls="${this.uuid}-list"
          aria-describedby="${this.uuid}-label"
          tabindex=${this.disabled ? '-1' : '0'}
        >
          <span class="button__label">${this.ctx === 'search' ? this.label : ''}${this.selectedLabel}</span>
          <slot name="icon"></slot>
        </button>
        <slot name="input"></slot>
        <hh-focus-trap active="${this._active}">
          <ul
            id="${this.uuid}-list"
            class="${classMap({
              list: true,
              'is-active': this._active,
              'position-top': this.position === 'top',
            })}"
            aria-hidden="${!this._active}"
            role="list"
          >
            ${this.options.map((option) => {
              this.lang && (option.lang = option.value)
              return html`<li
                class="${classMap({
                  option: true,
                  'is-active': option.value === this._selected,
                })}"
              >
                <a
                  @click="${(e) => this.selectValue(e, option.value)}"
                  class="link"
                  href="#"
                  hreflang="${ifDefined(option.lang)}"
                  lang="${ifDefined(option.lang)}"
                >
                  <span class="link-inner">
                    <span class="link-toggle" aria-hidden="true"></span>
                    <span class="link-text">
                      ${this.sanitize(option.label)}
                    </span>
                  </span>
                </a>
              </li>`
            })}
          </ul>
        </hh-focus-trap>
      </div>
    `
  }

  static styles = css`
    :host {
      width: 100%;
    }

    .container {
      position: relative;
    }

    .button {
      align-items: center;
      background: var(--hh-background, transparent);
      border: var(--hh-border-width, 1px) solid
        var(--hh-color-border, currentColor);
      cursor: pointer;
      display: flex;
      font: var(--hh-body-font);
      height: var(--hh-height, 35px);
      justify-content: space-between;
      padding: 0 1.7rem 0 1rem;
      width: 100%;
    }

    .button:hover,
    .button[aria-expanded='true'] {
      border-color: var(--hh-color-border-hover, currentColor);
    }

    .button:focus-visible {
      outline: 0.2rem solid var(--hh-color-focus, currentColor);
    }

    :host([disabled]) .button {
      cursor: default;
      border: var(--hh-border-width, 1px) solid var(--hh-color-disabled);
    }

    .button ::slotted([slot='icon']) {
      transition: transform var(--hh-duration-default, 300ms);
      color: var(--hh-label-button-color, var(--hh-color-text));
    }

    :host([disabled]) ::slotted([slot='icon']) {
      color: var(--hh-color-disabled);
    }

    .button[aria-expanded='true'] ::slotted([slot='icon']) {
      transform: rotate(180deg);
    }

    .button__label {
      position: relative;
      top: 8px;
      margin: 0.2rem 0 0;
      color: var(--hh-label-button-color, var(--hh-color-text));
      text-transform: var(--hh-text-transform, unset);
    }

    :host([disabled]) .button__label {
      color: var(--hh-color-disabled);
    }

    ::slotted([slot='button']),
    ::slotted([slot='icon']) {
      align-items: center;
      display: flex;
      height: 100%;
    }

    ::slotted([slot='icon']) {
      margin-left: 30px;
    }

    ::slotted([slot='title']) {
      color: var(--hh-label-color);
      font: var(--hh-label-font);
      left: 1rem;
      margin: 0;
      position: absolute;
      top: 8px;
      pointer-events: none;
      text-transform: var(--hh-text-transform, unset);
    }

    :host([disabled]) ::slotted([slot='title']) {
      color: var(--hh-color-disabled);
    }

    .list {
      background-color: var(--hh-background, white);
      border: var(--hh-border-width, 1px) solid
        var(--hh-options-color-border, currentColor);
      border-radius: var(--hh-options-border-radius, 0.3rem);
      box-shadow: var(--hh-options-box-shadow, none);
      display: block;
      margin: 0;
      max-height: var(--hh-options-max-height, 19rem);
      max-width: var(--hh-options-max-width, 22rem);
      min-height: var(--hh-options-min-height, 8.2rem);
      min-width: var(--hh-options-min-width, 12rem);
      opacity: 0;
      overflow-y: auto;
      padding-left: 0;
      position: absolute;
      transform: translateY(0);
      transition: visibility var(--hh-duration-default, 300ms)
          var(--hh-duration-default, 300ms),
        opacity var(--hh-duration-default, 300ms),
        transform var(--hh-duration-default, 300ms);
      visibility: hidden;
      width: 100%;
      z-index: 1;
    }

    .list.position-top {
      bottom: 100%;
    }

    .list.is-active {
      opacity: 1;
      transform: translateY(3px);
      transition: transform var(--hh-duration-default, 300ms) 1ms,
        opacity var(--hh-duration-default, 300ms) 1ms, visibility 1ms;
      visibility: visible;
    }

    .option {
      list-style: none;
      color: var(--hh-option-color, inherit);
    }

    .option:hover {
      color: var(--hh-option-color-hover, inherit);
    }

    .option.is-active {
      color: var(--hh-option-color-active, inherit);
    }

    .option:not(:last-of-type) .link {
      border-bottom: var(--hh-option-border-width, 1px) solid
        var(--hh-option-border-color, currentColor);
    }

    .link {
      color: inherit;
      display: block;
      padding: var(--hh-link-padding, 1em);
      text-decoration: none;
    }

    .link:focus-visible {
      color: var(--hh-option-color-hover);
      background-color: var(--hh-option-background-hover, inherit);
      outline: 0.2rem solid var(--hh-color-focus, currentColor);
      outline-offset: -0.2rem;
    }

    .link-inner {
      display: flex;
      align-items: center;
      column-gap: 1rem;
    }

    .link-toggle {
      width: 1.6rem;
      height: 1.6rem;
      color: inherit;
      border: var(--hh-border-width, 1px) solid currentColor;
      border-radius: 50%;
      position: relative;
    }

    .link-toggle::before {
      content: '';
      background-color: currentColor;
      width: 0.93rem;
      height: 0.93rem;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      border-radius: 50%;
      scale: 0;
    }

    .option.is-active .link-toggle::before {
      scale: 1;
    }

    .link-text {
      transform: translateY(-2px);
      text-transform: var(--hh-text-transform, unset);
    }

    :host([ctx='variant']) ::slotted([slot='title']),
    :host([ctx='search']) ::slotted([slot='title']) {
      display: none;
    }

    :host([ctx='variant']) .button__label,
    :host([ctx='search']) .button__label {
      top: unset;
      margin-top: 0;
      font: var(--hh-h6-font);
    }

    :host([ctx='variant']) .button,
    :host([ctx='search']) .button {
      padding: 1rem;
      gap: 1.5rem;
    }

    :host([ctx='variant']) ::slotted([slot='icon']),
    :host([ctx='search']) ::slotted([slot='icon']) {
      margin-left: 0;
    }

    :host([ctx='variant']) .list.position-top,
    :host([ctx='search']) .list.position-top {
      bottom: calc(100% + 8px);
    }

    :host([ctx='variant']) .link-toggle,
    :host([ctx='search']) .link-toggle {
      display: none;
    }

    :host([ctx='variant']) .option.is-active,
    :host([ctx='search']) .option.is-active {
      background-color: var(--hh-option-background-active, inherit);
    }

    :host([ctx='variant']) .option:hover,
    :host([ctx='search']) .option:hover {
      color: var(--hh-option-color-active, inherit);
      background-color: var(--hh-option-background-active, inherit);
    }

    :host([ctx='filter']) ::slotted([slot='title']) {
      display: inline-block;
      text-transform: uppercase;
      top: 50%;
      transform: translateY(-50%);
      color: var(--hh-color-night);
    }

    :host([ctx='filter']) .button {
      padding: 0px 1rem 0px 9rem;
      align-items: center;
      text-transform: uppercase;
      border: none;
    }

    :host([ctx='filter']) .button__label {
      top: unset;
      margin-top: 0;
      font: var(--hh-h6-font);
      width: max-content;
    }

    :host([ctx='filter']) ::slotted([slot='icon']) {
      margin-left: 1.5rem;
    }

    :host([ctx='filter']) .list {
      z-index: 2;
      border: none;
    }

    :host([ctx='filter']) .option .link {
      border-bottom: none;
      text-transform: uppercase;
      color: var(--hh-color-night);
    }

    :host([ctx='filter']) .link-toggle {
      display: none;
    }

    :host([ctx='filter']) .option:hover,
    :host([ctx='filter']) .option.is-active {
      color: var(--hh-color-day);
      background-color: var(--hh-color-night);
    }

    :host([ctx='filter']) .option:hover .link,
    :host([ctx='filter']) .option.is-active .link {
      color: var(--hh-color-day);
    }
  `
}

customElements.define('hh-custom-select', HHCustomSelect)
